import React, { useEffect, useState } from 'react';
import axios from '../../config/axiosConfig';
import { MdAdd, MdDelete, MdEdit, MdCategory, MdEvent } from 'react-icons/md';

const EventsManager = ({ onAddClick, onEditClick }) => {
    const [events, setEvents] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [newCategoryName, setNewCategoryName] = useState('');
    const [categoryError, setCategoryError] = useState(null);
    const [activeTab, setActiveTab] = useState('events'); // 'events' | 'categories'

    useEffect(() => {
        fetchEvents();
        fetchCategories();
    }, []);

    const fetchEvents = async () => {
        try {
            const response = await axios.get('nai-events/events/');
            setEvents(response.data);
            setLoading(false);
        } catch (error) {
            console.error("Error fetching events:", error);
            setLoading(false);
        }
    };

    const fetchCategories = async () => {
        try {
            const response = await axios.get('nai-events/categories/');
            setCategories(response.data);
        } catch (error) {
            console.error("Error fetching categories:", error);
        }
    };

    const handleDeleteEvent = async (id) => {
        if (!window.confirm("Are you sure you want to delete this event?")) return;
        try {
            await axios.delete(`nai-events/events/${id}/`);
            setEvents(prev => prev.filter(item => item.id !== id));
        } catch (error) {
            console.error("Error deleting event:", error);
            alert("Failed to delete event.");
        }
    };

    const handleCreateCategory = async (e) => {
        e.preventDefault();
        if (!newCategoryName.trim()) return;
        setCategoryError(null);
        try {
            const response = await axios.post('nai-events/categories/', { name: newCategoryName });
            setCategories(prev => [...prev, response.data].sort((a, b) => a.name.localeCompare(b.name)));
            setNewCategoryName('');
        } catch (error) {
            console.error("Error creating category:", error);
            setCategoryError("Category already exists or invalid data.");
        }
    };

    const handleDeleteCategory = async (id) => {
        if (!window.confirm("Deleting this category will set associated events to uncategorized. Continue?")) return;
        try {
            await axios.delete(`nai-events/categories/${id}/`);
            setCategories(prev => prev.filter(cat => cat.id !== id));
            // Refresh events to show updated categories
            fetchEvents();
        } catch (error) {
            console.error("Error deleting category:", error);
            alert("Failed to delete category.");
        }
    };

    if (loading) return <div className="p-6 text-center text-gray-500 font-medium">Loading events...</div>;

    return (
        <div className="bg-white rounded-2xl shadow-xl p-6 border border-gray-100">
            {/* Header Tabs */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center border-b pb-4 mb-6 gap-4">
                <div className="flex space-x-2 bg-gray-100 p-1 rounded-xl">
                    <button
                        onClick={() => setActiveTab('events')}
                        className={`flex items-center px-4 py-2 text-sm font-semibold rounded-lg transition-all ${
                            activeTab === 'events'
                                ? 'bg-white text-blue-600 shadow-sm'
                                : 'text-gray-600 hover:text-gray-900'
                        }`}
                    >
                        <MdEvent className="mr-2 w-4 h-4" /> Manage Events
                    </button>
                    <button
                        onClick={() => setActiveTab('categories')}
                        className={`flex items-center px-4 py-2 text-sm font-semibold rounded-lg transition-all ${
                            activeTab === 'categories'
                                ? 'bg-white text-blue-600 shadow-sm'
                                : 'text-gray-600 hover:text-gray-900'
                        }`}
                    >
                        <MdCategory className="mr-2 w-4 h-4" /> Manage Categories
                    </button>
                </div>

                {activeTab === 'events' && (
                    <button
                        onClick={onAddClick}
                        className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-bold px-5 py-2.5 rounded-xl flex items-center shadow-lg transition-all transform hover:scale-105"
                    >
                        <MdAdd className="mr-2 w-5 h-5" /> Add New Event
                    </button>
                )}
            </div>

            {/* Tab: Events */}
            {activeTab === 'events' && (
                <div className="overflow-x-auto rounded-xl border border-gray-150">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Image</th>
                                <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Title</th>
                                <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Category</th>
                                <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Date</th>
                                <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {events.map((event) => {
                                const primaryImage = event.images && event.images.length > 0 ? event.images[0].image : null;
                                return (
                                    <tr key={event.id} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            {primaryImage ? (
                                                <img src={primaryImage} alt={event.title} className="h-14 w-20 object-cover rounded-lg shadow-sm border border-gray-100" />
                                            ) : (
                                                <div className="h-14 w-20 bg-gray-100 rounded-lg flex items-center justify-center text-gray-400 text-xs italic">
                                                    No Images
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-800">{event.title}</td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`px-2.5 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                                event.category_detail ? 'bg-indigo-50 text-indigo-700' : 'bg-gray-100 text-gray-600'
                                            }`}>
                                                {event.category_detail ? event.category_detail.name : 'Uncategorized'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {new Date(event.created_at).toLocaleDateString()}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold">
                                            <button 
                                                onClick={() => onEditClick(event.id)}
                                                className="text-blue-600 hover:text-blue-900 mr-4 inline-flex items-center"
                                            >
                                                <MdEdit className="mr-1" /> Edit
                                            </button>
                                            <button 
                                                onClick={() => handleDeleteEvent(event.id)}
                                                className="text-red-600 hover:text-red-950 inline-flex items-center"
                                            >
                                                <MdDelete className="mr-1" /> Delete
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    {events.length === 0 && (
                        <div className="text-center py-10 text-gray-400 italic">No events found. Build your first event above!</div>
                    )}
                </div>
            )}

            {/* Tab: Categories */}
            {activeTab === 'categories' && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Create Category Form */}
                    <div className="bg-gray-50 p-6 rounded-2xl border border-gray-100 h-fit">
                        <h3 className="text-base font-bold text-gray-800 mb-4 flex items-center">
                            <MdAdd className="mr-2 text-blue-500 w-5 h-5" /> Add New Category
                        </h3>
                        {categoryError && (
                            <p className="text-red-600 text-xs mb-3">{categoryError}</p>
                        )}
                        <form onSubmit={handleCreateCategory} className="space-y-4">
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Category Name</label>
                                <input
                                    type="text"
                                    required
                                    placeholder="e.g. Press Meet 2026"
                                    value={newCategoryName}
                                    onChange={(e) => setNewCategoryName(e.target.value)}
                                    className="w-full rounded-xl border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 border p-2.5 text-sm"
                                />
                            </div>
                            <button
                                type="submit"
                                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-2.5 rounded-xl transition-all shadow-md text-sm"
                            >
                                Save Category
                            </button>
                        </form>
                    </div>

                    {/* Category List */}
                    <div className="lg:col-span-2 border border-gray-150 rounded-2xl overflow-hidden bg-white">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Category Name</th>
                                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Slug</th>
                                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {categories.map((cat) => (
                                    <tr key={cat.id} className="hover:bg-gray-55 transition-colors">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-800">{cat.name}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{cat.slug}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                                            <button 
                                                onClick={() => handleDeleteCategory(cat.id)}
                                                className="text-red-600 hover:text-red-950 inline-flex items-center font-semibold"
                                            >
                                                <MdDelete className="mr-1" /> Delete
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {categories.length === 0 && (
                            <div className="text-center py-10 text-gray-400 italic">No categories created yet.</div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default EventsManager;
